import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_UI,
  FONT_HEADING,
} from "../layouts/constants";

export default function SentinelGuardList() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    company_name: "",
    licensedFor: "",
    admin_active: 0,
    company_address: "",
    company_phone: "",
    admin_email: "",
    company_logo: "",
    user_map: "",
  });

  const [guards, setGuards] = useState([]);
  const [incidentTypes, setIncidentTypes] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [search, setSearch] = useState("");

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const token =
      localStorage.getItem("token") || localStorage.getItem("authToken");
    const isLoggedIn = localStorage.getItem("isLoggedIn");
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");

    if (!token || !isLoggedIn) {
      navigate("/");
      return;
    }

    const local = {
      company_name:
        localStorage.getItem("company_name") || userData.company_name || "",
      licensedFor:
        localStorage.getItem("licensedFor") || userData.licensedFor || "",
      admin_active: Number(
        localStorage.getItem("admin_active") ?? userData.admin_active ?? 0
      ),
      company_address:
        localStorage.getItem("company_address") ||
        userData.company_address ||
        "",
      company_phone:
        localStorage.getItem("company_phone") || userData.company_phone || "",
      admin_email:
        localStorage.getItem("admin_email") || userData.admin_email || "",
      company_logo:
        localStorage.getItem("company_logo") || userData.company_logo || "",
      user_map: localStorage.getItem("user_map") || userData.user_map || "",
    };

    setDashboardData(local);
    fetchPageData(local, token);
  }, [navigate]);

  useEffect(() => {
    if (!successMessage && !errorMessage) return;

    const timer = setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 5000);

    return () => clearTimeout(timer);
  }, [successMessage, errorMessage]);

  const fetchPageData = async (local, token) => {
    try {
      setLoading(true);
      setErrorMessage("");

      const [dashboardRes, guardsRes, typesRes] = await Promise.all([
        fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),

        fetch(`${API_BASE_URL}/incidents/sentinel-guards`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),

        fetch(`${API_BASE_URL}/incidents/types`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }),
      ]);

      if (!dashboardRes.ok) throw new Error("Failed to fetch dashboard");
      if (!guardsRes.ok) throw new Error("Failed to fetch sentinel guards");
      if (!typesRes.ok) throw new Error("Failed to fetch incident types");

      const dashboardResult = await dashboardRes.json();
      const guardsResult = await guardsRes.json();
      const typesResult = await typesRes.json();

      const companyDetails = dashboardResult?.data?.companyDetails || {};

      setDashboardData({
        ...local,
        company_name: companyDetails.company_name ?? local.company_name ?? "",
        licensedFor: companyDetails.licensedFor ?? local.licensedFor ?? "",
        admin_active: Number(
          companyDetails.admin_active ?? local.admin_active ?? 0
        ),
        company_address:
          companyDetails.company_address ?? local.company_address ?? "",
        company_phone: companyDetails.company_phone ?? local.company_phone ?? "",
        admin_email: companyDetails.admin_email ?? local.admin_email ?? "",
        company_logo: companyDetails.company_logo ?? local.company_logo ?? "",
        user_map: companyDetails.user_map ?? local.user_map ?? "",
      });

      const guardList = Array.isArray(guardsResult?.data)
        ? guardsResult.data
        : [];

      const typeList = Array.isArray(typesResult?.data)
        ? typesResult.data
        : [];

      setGuards(guardList);
      setIncidentTypes(typeList);

      await fetchAllGuardPermissions(guardList, token);
    } catch (error) {
      console.error("Sentinel guard list page error:", error);
      setErrorMessage(error.message || "Failed to load sentinel guard list.");
    } finally {
      setLoading(false);
    }
  };

  const fetchAllGuardPermissions = async (guardList, token) => {
    try {
      const permissionMap = {};

      await Promise.all(
        guardList.map(async (guard) => {
          if (!guard.member_id) return;

          const res = await fetch(
            `${API_BASE_URL}/sentinel_permission/${encodeURIComponent(
              guard.member_id
            )}`,
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (!res.ok) {
            permissionMap[guard.member_id] = {};
            return;
          }

          const data = await res.json();
          const selectedIds = Array.isArray(data?.selectedIds)
            ? data.selectedIds
            : [];

          permissionMap[guard.member_id] = {};

          selectedIds.forEach((id) => {
            permissionMap[guard.member_id][Number(id)] = true;
          });
        })
      );

      setPermissions(permissionMap);
    } catch (error) {
      console.error("Fetch sentinel permission error:", error);
      setPermissions({});
    }
  };

  const routePage = useMemo(
    () =>
      dashboardData.user_map === "google"
        ? "/route-master"
        : "/route-master1",
    [dashboardData.user_map]
  );

  const filteredGuards = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    if (!keyword) return guards;

    return guards.filter((guard) => {
      const memberId = String(guard.member_id || "").toLowerCase();
      const guardId = String(guard.guard_id || "").toLowerCase();
      const name = String(guard.name || "").toLowerCase();
      const username = String(guard.username || "").toLowerCase();
      const role = String(guard.role || "").toLowerCase();
      const designation = String(guard.designation || "").toLowerCase();

      return (
        memberId.includes(keyword) ||
        guardId.includes(keyword) ||
        name.includes(keyword) ||
        username.includes(keyword) ||
        role.includes(keyword) ||
        designation.includes(keyword)
      );
    });
  }, [guards, search]);

  const togglePermission = (memberId, typeId) => {
    setPermissions((prev) => ({
      ...prev,
      [memberId]: {
        ...(prev[memberId] || {}),
        [typeId]: !prev?.[memberId]?.[typeId],
      },
    }));
  };

  const backToIncidents = () => navigate("/add_incidents");

  const handleSavePermissions = async () => {
    try {
      setSaving(true);
      setSuccessMessage("");
      setErrorMessage("");

      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");

      await Promise.all(
        guards.map(async (guard) => {
          if (!guard.member_id) return;

          const typeIds = incidentTypes
            .filter((type) => permissions?.[guard.member_id]?.[type.id])
            .map((type) => Number(type.id))
            .filter((id) => Number.isFinite(id));

          const res = await fetch(
            `${API_BASE_URL}/sentinel_permission/${encodeURIComponent(
              guard.member_id
            )}/save`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                type_ids: typeIds,
              }),
            }
          );

          const data = await res.json();

          if (!res.ok || data.success === false) {
            throw new Error(
              data.message ||
                `Failed to save permission for ${guard.member_id}`
            );
          }
        })
      );

      setSuccessMessage("Sentinel guard permissions saved successfully.");
      setTimeout(() => {
  navigate("/add_incidents");
}, 1000);
    } catch (error) {
      console.error("Save sentinel permission error:", error);
      setErrorMessage(error.message || "Failed to save permissions.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin mb-3" />
        <p className="text-slate-500" style={{ fontFamily: FONT_UI }}>
          Loading sentinel guard list...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="incidents"
      activeMenu="incidents"
    >
      <div className="mb-6">
        <h2
          className="m-0 text-[26px] font-normal text-slate-900 tracking-tight"
          style={{ fontFamily: FONT_HEADING }}
        >
          Sentinel Guard List
        </h2>

        <p
          className="mt-1 text-sm text-slate-500"
          style={{ fontFamily: FONT_UI }}
        >
          Add incident type permissions for sentinel guards
        </p>
      </div>

      {successMessage && <Alert type="success" message={successMessage} />}
      {errorMessage && <Alert type="error" message={errorMessage} />}

      <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-6">
        <div className="flex justify-between items-center gap-4 flex-wrap mb-5">
          <div>
            <h3
              className="m-0 text-[22px] font-normal text-slate-900"
              style={{ fontFamily: FONT_HEADING }}
            >
              Sentinel Guard Permission Management
            </h3>

            <p
              className="mt-1 text-sm text-slate-500"
              style={{ fontFamily: FONT_UI }}
            >
              Select which incident types each sentinel guard can access
            </p>
          </div>

         <div className="flex gap-3">
  <button
    type="button"
    onClick={backToIncidents}
    className="px-5 py-2.5 rounded-xl border border-slate-300 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
    style={{ fontFamily: FONT_UI }}
  >
    Back
  </button>

  <button
    type="button"
    onClick={handleSavePermissions}
    disabled={saving}
    className="px-5 py-2.5 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-70 disabled:cursor-not-allowed"
    style={{ fontFamily: FONT_UI }}
  >
    {saving ? "Saving..." : "Save Permission"}
  </button>
</div>
        </div>

        <div className="mb-5">
          <input
            type="text"
            placeholder="Search by member ID, guard ID, name, username, role..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full px-3.5 py-3 rounded-xl border border-slate-200 outline-none text-sm text-slate-900 bg-white focus:ring-2 focus:ring-blue-100 focus:border-blue-500"
            style={{ fontFamily: FONT_UI }}
          />
        </div>

        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table
            className="w-full min-w-[1200px] border-collapse"
            style={{ fontFamily: FONT_UI }}
          >
            <thead>
              <tr className="bg-slate-900 text-white">
                <Th>Member ID</Th>
                <Th>Guard ID</Th>
                <Th>Guard</Th>
                <Th>Username</Th>
                <Th>Designation</Th>
                <Th>Status</Th>
                <Th>Incident Type Permission</Th>
              </tr>
            </thead>

            <tbody>
              {filteredGuards.length === 0 ? (
                <tr>
                  <td colSpan={7} className="p-6 text-center text-slate-500">
                    No sentinel guards found.
                  </td>
                </tr>
              ) : (
                filteredGuards.map((guard, index) => (
                  <tr
                    key={`${guard.member_id}-${guard.guard_id || index}`}
                    className="bg-white hover:bg-slate-50"
                  >
                    <Td>
                      <span className="text-blue-600 font-bold">
                        {guard.member_id || "-"}
                      </span>
                    </Td>

                    <Td>{guard.guard_id || "-"}</Td>

                    <Td>
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-blue-600 text-white flex items-center justify-center font-bold">
                          {String(guard.name || "?").charAt(0).toUpperCase()}
                        </div>

                        <div className="font-semibold text-slate-900">
                          {guard.name || "-"}
                        </div>
                      </div>
                    </Td>

                    <Td>{guard.username || "-"}</Td>

                    <Td>
                      <span className="inline-flex px-3 py-1.5 rounded-full bg-green-50 text-green-700 text-xs font-semibold">
                        {guard.designation || guard.role || "Guard"}
                      </span>
                    </Td>

                    <Td>
                      <span
                        className={`inline-flex px-3 py-1.5 rounded-full text-xs font-semibold ${
                          guard.is_active === true ||
                          guard.is_active === 1 ||
                          guard.is_active === "1"
                            ? "bg-green-50 text-green-700"
                            : "bg-red-50 text-red-700"
                        }`}
                      >
                        {guard.is_active === true ||
                        guard.is_active === 1 ||
                        guard.is_active === "1"
                          ? "Active"
                          : "Inactive"}
                      </span>
                    </Td>

                    <Td>
                      {incidentTypes.length === 0 ? (
                        <span className="text-slate-400 text-sm">
                          No incident types found
                        </span>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {incidentTypes.map((type) => {
                            const checked =
                              !!permissions?.[guard.member_id]?.[type.id];

                            return (
                              <label
                                key={`${guard.member_id}-${type.id}`}
                                className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl border text-xs font-semibold cursor-pointer transition-all ${
                                  checked
                                    ? "bg-blue-50 border-blue-300 text-blue-700"
                                    : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() =>
                                    togglePermission(guard.member_id, type.id)
                                  }
                                  className="h-4 w-4"
                                />

                                <span className="text-sm">
                                  {type.icon_value || "⚠️"}
                                </span>

                                <span>{type.label || type.type_key}</span>
                              </label>
                            );
                          })}
                        </div>
                      )}
                    </Td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}

function Alert({ type, message }) {
  const isSuccess = type === "success";

  return (
    <div
      className={`mb-5 px-4 py-3.5 rounded-xl text-sm font-medium border ${
        isSuccess
          ? "bg-green-50 border-green-200 text-green-700"
          : "bg-red-50 border-red-200 text-red-700"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      {message}
    </div>
  );
}

function Th({ children }) {
  return (
    <th className="text-left px-4 py-3.5 text-sm font-semibold">
      {children}
    </th>
  );
}

function Td({ children }) {
  return (
    <td className="px-4 py-3.5 border-b border-slate-200 text-sm text-slate-700 align-top">
      {children}
    </td>
  );
}